import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { instituteService } from '../../../services/instituteService';

const ExhibitionHallPreview = () => {
  const navigate = useNavigate();
  const [institutes, setInstitutes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPreviewInstitutes();
  }, []);

  const fetchPreviewInstitutes = async () => {
    try {
      setLoading(true);
      const result = await instituteService?.getExhibitionHallInstitutes({
        page: 1,
        pageSize: 8
      });
      const transformed = result?.data?.map(i => ({
        id: i?.id,
        name: i?.instituteName || 'Unknown Institute',
        logo: i?.logoUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_1e7bb1524-1766485632262.png',
        banner: i?.bannerUrl || 'https://img.rocket.new/generatedImages/rocket_gen_img_1f477cd97-1768735565694.png',
        categories: i?.categories || [],
        state: i?.state,
        boothType: i?.boothType || 'free',
        visitors: i?.virtualBooths?.[0]?.visit_count || 0,
      })) || [];
      setInstitutes(transformed);
    } catch (err) {
      console.error('Error fetching preview institutes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = () => {
    navigate('/student-registration');
  };

  return (
    <section className="py-16 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-cyan-50 border border-cyan-200 px-4 py-2 rounded-full mb-4">
            <Icon name="Building2" size={16} color="#0891b2" />
            <span className="text-sm font-semibold text-cyan-700 uppercase tracking-wide">Virtual Exhibition Hall</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-3">
            Explore Top Institutes
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Browse institutes, view courses, and explore booths freely. Register to chat, book sessions, and connect directly.
          </p>
        </div>

        {/* Institute Grid */}
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {Array.from({ length: 8 })?.map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-md overflow-hidden animate-pulse">
                <div className="h-32 bg-gray-200" />
                <div className="p-3">
                  <div className="h-4 bg-gray-200 rounded mb-2" />
                  <div className="h-3 bg-gray-100 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {institutes?.map((institute) => (
              <div
                key={institute?.id}
                className="group relative bg-white rounded-xl shadow-md overflow-hidden border border-border hover:shadow-xl hover:border-cyan-300 transition-all duration-300"
              >
                {/* Banner */}
                <div className="relative h-32 overflow-hidden">
                  <img
                    src={institute?.banner}
                    alt={`${institute?.name} campus`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />

                  {institute?.boothType === 'premium' && (
                    <div className="absolute top-2 left-2 bg-yellow-500 text-yellow-900 px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1">
                      <Icon name="Crown" size={11} color="currentColor" />
                      PREMIUM
                    </div>
                  )}

                  {/* Logo */}
                  <div className="absolute bottom-2 left-2">
                    <div className="w-10 h-10 bg-white rounded-lg p-1 shadow-md">
                      <img
                        src={institute?.logo}
                        alt={`${institute?.name} logo`}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-3">
                  <h3 className="text-sm font-bold text-foreground mb-1 line-clamp-2 group-hover:text-cyan-700 transition-colors">
                    {institute?.name}
                  </h3>
                  {institute?.state && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                      <Icon name="MapPin" size={11} color="var(--color-muted-foreground)" />
                      {institute?.state}
                    </p>
                  )}
                  {institute?.categories?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {institute?.categories?.slice(0, 2)?.map((cat) => (
                        <span key={cat} className="px-1.5 py-0.5 rounded-full text-xs bg-cyan-50 text-cyan-700 border border-cyan-100">
                          {cat}
                        </span>
                      ))}
                    </div>
                  )}
                  {/* View Details — requires registration */}
                  <button
                    onClick={handleViewDetails}
                    className="w-full mt-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-gradient-to-r from-cyan-600 to-violet-600 rounded-lg hover:shadow-md hover:-translate-y-0.5 transition-all"
                  >
                    View Details
                    <Icon name="Lock" size={11} color="white" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA Banner */}
        <div className="relative bg-gradient-to-r from-cyan-600 to-violet-700 rounded-2xl p-8 text-white text-center overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
          </div>
          <div className="relative z-10">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Icon name="Lock" size={20} color="white" />
              <span className="text-sm font-semibold uppercase tracking-wider opacity-90">Full Access After Registration</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-heading font-bold mb-2">
              Register Free to Unlock Everything
            </h3>
            <p className="text-white/80 mb-6 max-w-xl mx-auto">
              Chat with counsellors, book 1-on-1 sessions, send WhatsApp messages, and request callbacks — all after a quick free registration.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate('/student-registration')}
                className="px-8 py-3 bg-white text-violet-700 font-bold rounded-xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all text-sm"
              >
                Register Free — It's Quick!
              </button>
              <button
                onClick={() => navigate('/exhibition-hall')}
                className="px-8 py-3 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-xl border border-white/30 transition-all text-sm flex items-center gap-2"
              >
                <Icon name="Eye" size={16} color="white" />
                Browse Exhibition Hall
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExhibitionHallPreview;
